'use client';

/**
 * NotificationDropdown
 *
 * Bell icon dropdown showing recent notifications with unread badge.
 * Polls /api/notifications/unread-count every 30s.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  Bell,
  Check,
  CheckCheck,
  Briefcase,
  UserPlus,
  Video,
  Shield,
  Mail,
  Loader2,
} from 'lucide-react';
import { Button, IconButton } from '@/components/ui/button';
import { API_BASE } from '@/lib/config';

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  is_read: boolean;
  action_url: string | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

const TYPE_ICONS: Record<string, typeof Bell> = {
  application_received: UserPlus,
  stage_changed: Briefcase,
  interview_scheduled: Video,
  interview_completed: Check,
  interview_analysis_ready: CheckCheck,
  assessment_completed: Shield,
  assessment_invited: Shield,
  system: Bell,
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface NotificationDropdownProps {
  token: string | null;
}

export function NotificationDropdown({ token }: NotificationDropdownProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  }), [token]);

  // Poll unread count.
  // - Backoff doubles on consecutive failures (capped at 5 min) so a flaky
  //   gateway doesn't burn QPS at a fixed cadence.
  // - Pauses when the tab is hidden; resumes on visibilitychange. Two open
  //   tabs no longer poll in parallel.
  useEffect(() => {
    if (!token) return;

    const BASE_INTERVAL_MS = 30_000;
    const MAX_INTERVAL_MS = 5 * 60_000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let nextDelay = BASE_INTERVAL_MS;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${API_BASE}/notifications/unread-count`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
          nextDelay = BASE_INTERVAL_MS;
        } else {
          nextDelay = Math.min(nextDelay * 2, MAX_INTERVAL_MS);
        }
      } catch {
        nextDelay = Math.min(nextDelay * 2, MAX_INTERVAL_MS);
      }
      schedule();
    };

    const schedule = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      timeoutId = setTimeout(tick, nextDelay);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Resume immediately with a fresh fetch on tab refocus.
        if (timeoutId) clearTimeout(timeoutId);
        nextDelay = BASE_INTERVAL_MS;
        tick();
      } else if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    tick();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [token, headers]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (!isOpen || !token || hasLoaded) return;

    const loadNotifications = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE}/notifications?limit=15`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          setNotifications(data.notifications || []);
        }
      } catch {
        // Silently fail
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
      }
    };

    loadNotifications();
  }, [isOpen, token, hasLoaded, headers]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkRead = async (notificationId: string) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: headers(),
      });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'POST',
        headers: headers(),
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkRead(notification.id);
    }
    if (notification.action_url) {
      router.push(notification.action_url);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <IconButton
        label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        variant="ghost"
        className="rounded-full relative"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setHasLoaded(false); // Refresh on re-open
        }}
      >
        <Bell className="w-5 h-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </IconButton>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border rounded-xl shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleMarkAllRead}
                className="h-auto gap-1 p-0 text-xs"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = TYPE_ICONS[notification.type] || Bell;
                return (
                  <Button
                    key={notification.id}
                    type="button"
                    variant="ghost"
                    onClick={() => handleNotificationClick(notification)}
                    className={`h-auto w-full items-start justify-start gap-3 whitespace-normal rounded-none border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/50 ${
                      !notification.is_read ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg mt-0.5 flex-shrink-0 ${
                      !notification.is_read ? 'bg-primary/10' : 'bg-muted'
                    }`}>
                      <Icon className={`w-4 h-4 ${
                        !notification.is_read ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${
                        !notification.is_read ? 'font-medium' : ''
                      }`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.body}
                      </p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">
                        {timeAgo(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                    )}
                  </Button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
